import { Component } from 'react';
import { Box } from 'mui/core';
import isChunkLoadError from '@shared/ui/ErrorBoundary/isChunkLoadError';
import LoadingErrorWidget from './LoadingErrorWidget';

// Mirrors the FSD ErrorBoundary's stale-chunk recovery so a fresh
// deploy that lands while a tab is open doesn't strand the user on
// the "Oops" screen. Debounced via sessionStorage — one reload
// attempt; if the second render still throws a ChunkLoadError the
// bundle is genuinely broken and we fall through to the fallback.
const CHUNK_RELOAD_KEY = 'qhs:chunk-reload-at';
const CHUNK_RELOAD_DEBOUNCE_MS = 10000;

const justReloadedForChunk = () => {
  try {
    const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    return Date.now() - last < CHUNK_RELOAD_DEBOUNCE_MS;
  } catch {
    return false;
  }
};

const markChunkReload = () => {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage unavailable — the error UI will show instead */
  }
};

const ErrorFallback = ({ onReset }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        p: 4,
      }}
    >
      <LoadingErrorWidget onReload={onReset} />
    </Box>
  );
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, recoveringChunk: false };
  }

  static getDerivedStateFromError(error) {
    const recoveringChunk = isChunkLoadError(error) && !justReloadedForChunk();
    return { hasError: true, error, recoveringChunk };
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error) && !justReloadedForChunk()) {
      markChunkReload();
      window.location.reload();
      return;
    }
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, recoveringChunk: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Reload in flight — blank frame beats the "Oops" flash.
      if (this.state.recoveringChunk) return null;
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorFallback onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
